import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiFileText, FiSearch, FiCalendar } from "react-icons/fi";
import { DueDiligenceDocument } from "../../Common/Types-DTOs/DueDiligence";
import { formatRelativeDate, formatFullDate } from "../../Common/Utils/dateUtils";
import { DataTable, Column } from "../DataTable";
import {
  DocumentsTableWrapper,
  SearchInput,
  SearchInputWrapper,
  FiltersContainer,
  FilterButton,
  TableToolbar,
  FileNameCell,
  FileName,
  DateCell,
  FilterDropdownContainer,
  DatePickerDropdown,
  DatePickerTitle,
  DatePickerFields,
  DateFieldLabel,
  DateInput,
  DatePickerActions,
  DatePickerButton,
  FilterCheckBadge,
} from "./Styles";

interface DocumentsTableProps {
  documents: DueDiligenceDocument[];
  onDocumentSelect: (document: DueDiligenceDocument) => void;
  selectedDocumentId?: string;
}

export const DocumentsTable = ({
  documents,
  onDocumentSelect,
}: DocumentsTableProps) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState<"date" | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("uploadDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  const handleFilterClick = () => {
    setShowDatePicker(!showDatePicker);
    setActiveFilter(showDatePicker ? null : "date");
  };

  const handleDateRangeChange = (field: "start" | "end", value: string) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const clearDateRange = () => {
    setDateRange({ start: "", end: "" });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-filter-dropdown]')) {
        setActiveFilter(null);
        setShowDatePicker(false);
      }
    };

    if (activeFilter || showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeFilter, showDatePicker]);

  const isUploadedByMike = (doc: DueDiligenceDocument) =>
    (doc.uploadedBy ?? '') === 'system' ||
    (doc.uploadedBy ?? '').startsWith('backoffice:');

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      base_document: t("common.documentsTable.documentTypes.base_document"),
      additional_document: t("common.documentsTable.documentTypes.additional_document"),
      contract: t("common.documentsTable.documentTypes.contract"),
      cadastral: t("common.documentsTable.documentTypes.cadastral"),
      technical_report: t("common.documentsTable.documentTypes.technical_report"),
      legal_document: t("common.documentsTable.documentTypes.legal_document"),
      financial_document: t("common.documentsTable.documentTypes.financial_document"),
      other: t("common.documentsTable.documentTypes.other"),
    };
    return labels[type] || type;
  };

  const handleSort = (field: string) => {
    if (sortColumn === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(field);
      setSortDirection("desc");
    }
  };

  const filteredAndSortedDocuments = useMemo(() => {
    let filtered = documents;

    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          (doc.publicName || doc.fileName).toLowerCase().includes(searchLower) ||
          getDocumentTypeLabel(doc.documentType).toLowerCase().includes(searchLower)
      );
    }

    // Filter by date range
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter((doc) => {
        const docDate = new Date(doc.uploadDate);
        docDate.setHours(0, 0, 0, 0);

        if (dateRange.start && dateRange.end) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(dateRange.end);
          endDate.setHours(0, 0, 0, 0);
          return docDate >= startDate && docDate <= endDate;
        }

        if (dateRange.start) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          return docDate >= startDate;
        }

        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(0, 0, 0, 0);
          return docDate <= endDate;
        }

        return true;
      });
    }

    // Sort documents
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case "fileName":
          comparison = a.fileName.localeCompare(b.fileName);
          break;
        case "uploadDate":
          comparison = new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime();
          break;
        default:
          comparison = 0;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [documents, searchText, dateRange, sortColumn, sortDirection]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, dateRange, sortColumn, sortDirection]);

  // Define columns for DataTable
  const columns: Column<DueDiligenceDocument>[] = [
    {
      key: "fileName",
      label: t("common.documentsTable.columns.fileName"),
      width: "99%",
      sortable: true,
      render: (doc) => (
        <FileNameCell>
          <FiFileText size={20} style={{ color: '#9CA3AF', flexShrink: 0 }} />
          <FileName title={doc.publicName || doc.fileName}>
            {doc.publicName || doc.fileName}
          </FileName>
          <span
            style={{
              marginLeft: '8px',
              fontSize: '0.6875rem',
              fontWeight: 600,
              padding: '2px 6px',
              borderRadius: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              flexShrink: 0,
              ...(isUploadedByMike(doc)
                ? { backgroundColor: '#DBEAFE', color: '#1E40AF' }
                : { backgroundColor: '#F3F4F6', color: '#6B7280' }),
            }}
          >
            {isUploadedByMike(doc) ? t("common.documentsTable.uploadedByMike") : t("common.documentsTable.uploadedByUser")}
          </span>
        </FileNameCell>
      ),
    },
    {
      key: "uploadDate",
      label: t("common.documentsTable.columns.uploadDate"),
      width: "1%",
      sortable: true,
      render: (doc) => {
        return (
          <DateCell title={formatFullDate(doc.uploadDate)}>
            {formatRelativeDate(doc.uploadDate)}
          </DateCell>
        );
      },
    },
  ];

  // Paginate documents
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedDocuments = filteredAndSortedDocuments.slice(startIndex, endIndex);

  return (
    <DocumentsTableWrapper>
      <TableToolbar>
        <SearchInputWrapper>
          <FiSearch className="search-icon" size={20} />
          <SearchInput
            type="text"
            placeholder={t("common.documentsTable.searchPlaceholder")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </SearchInputWrapper>

        <FiltersContainer>
          <span className="filter-label">{t("common.documentsTable.filterByLabel")}</span>

          <FilterDropdownContainer data-filter-dropdown>
            <FilterButton
              $active={activeFilter === "date" || dateRange.start !== "" || dateRange.end !== ""}
              onClick={() => handleFilterClick()}
            >
              {t("common.documentsTable.dateFilterButton")}
              <FiCalendar size={16} />
              {(dateRange.start || dateRange.end) && (
                <FilterCheckBadge>
                  ✓
                </FilterCheckBadge>
              )}
            </FilterButton>
            {showDatePicker && (
              <DatePickerDropdown data-filter-dropdown>
                <DatePickerTitle>
                  {t("common.documentsTable.datePickerTitle")}
                </DatePickerTitle>
                <DatePickerFields>
                  <div>
                    <DateFieldLabel>
                      {t("common.documentsTable.dateFieldFrom")}
                    </DateFieldLabel>
                    <DateInput
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => handleDateRangeChange("start", e.target.value)}
                      max={dateRange.end || undefined}
                    />
                  </div>
                  <div>
                    <DateFieldLabel>
                      {t("common.documentsTable.dateFieldTo")}
                    </DateFieldLabel>
                    <DateInput
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => handleDateRangeChange("end", e.target.value)}
                      min={dateRange.start || undefined}
                    />
                  </div>
                </DatePickerFields>
                {(dateRange.start || dateRange.end) && (
                  <DatePickerActions>
                    <DatePickerButton onClick={clearDateRange}>
                      {t("common.documentsTable.clearButton")}
                    </DatePickerButton>
                    <DatePickerButton $variant="primary" onClick={() => setShowDatePicker(false)}>
                      {t("common.documentsTable.applyButton")}
                    </DatePickerButton>
                  </DatePickerActions>
                )}
              </DatePickerDropdown>
            )}
          </FilterDropdownContainer>
        </FiltersContainer>
      </TableToolbar>

      <DataTable
        columns={columns}
        data={paginatedDocuments}
        onRowClick={onDocumentSelect}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalItems={filteredAndSortedDocuments.length}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={(rows) => {
          setRowsPerPage(rows);
          setCurrentPage(1);
        }}
        emptyMessage={t("common.documentsTable.emptyMessage")}
        getRowKey={(doc) => doc.id}
      />
    </DocumentsTableWrapper>
  );
};




